import React from 'react';
import { Upload, FileText, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { TableauRecapConsommations } from '../TableauRecapConsommations';

/**
 * Étape 5 du formulaire TICFE : saisie des consommations mensuelles par PDL
 * (mode mono ou multi-site, import Excel ou saisie manuelle).
 */
export const Step5Consommations = ({ ctx }) => {
  const {
    formData, rapportType, setStep, sauvegarderBrouillon,
    methodeConsommation, setMethodeConsommation,
    consommationsParPDL, setConsommationsParPDL,
    showTableauRecap, setShowTableauRecap,
    downloadTemplate, generateMonthsForPeriod,
    handleImportConsommationsOutil, handleImportConsommationsExcel,
    updateConsommation,
  } = ctx;

  return (
    <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#BFC6CC]">
        <button onClick={() => setStep(rapportType === 'mono' ? 3 : 4)} className="px-4 py-2 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9] text-sm">← Retour</button>
        <button type="button" onClick={sauvegarderBrouillon} className="px-4 py-2 border border-[#F9C80E] text-[#F9C80E] font-medium rounded-lg hover:bg-[#FFF9E6] text-sm">💾 Enregistrer Brouillon</button>
      </div>

      <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">Consommations Mensuelles</h2>

      <p className="text-[#687175] mb-6">
        Période fiscale : {formData.debut_periode ? new Date(formData.debut_periode).toLocaleDateString('fr-FR') : '-'}
        {' au '}
        {formData.fin_periode ? new Date(formData.fin_periode).toLocaleDateString('fr-FR') : '-'}
        {rapportType === 'mono' && ` • ${formData.pdls.length} PDL`}
        {rapportType === 'multi' && ` • ${formData.sites.length} sites, ${formData.sites.reduce((sum, s) => sum + s.pdls.length, 0)} PDL`}
      </p>

      {((rapportType === 'mono' && formData.pdls.length === 0) ||
        (rapportType === 'multi' && formData.sites.length === 0)) ? (
        <div className="p-12 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg text-center">
          <p className="text-[#687175] mb-4">{rapportType === 'mono' ? 'Aucun PDL ajouté' : 'Aucun site ajouté'}</p>
          <button onClick={() => setStep(rapportType === 'mono' ? 3 : 4)} className="text-[#397DFF] hover:text-[#4672B6] font-medium">← Retour pour ajouter</button>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4">
            <button type="button" onClick={() => setMethodeConsommation('excel')} className={`p-4 border-2 rounded-lg text-left transition-all ${methodeConsommation === 'excel' ? 'border-[#397DFF] bg-[#E8F4FD]' : 'border-[#BFC6CC] hover:border-[#397DFF]'}`}>
              <div className="flex items-center gap-3">
                <Upload className={`w-5 h-5 ${methodeConsommation === 'excel' ? 'text-[#397DFF]' : 'text-[#687175]'}`} />
                <div>
                  <div className="font-medium text-[#191D1E]">Import Excel</div>
                  <div className="text-xs text-[#687175]">Charger consommations depuis fichier</div>
                </div>
              </div>
            </button>
            <button type="button" onClick={() => setMethodeConsommation('manuel')} className={`p-4 border-2 rounded-lg text-left transition-all ${methodeConsommation === 'manuel' ? 'border-[#397DFF] bg-[#E8F4FD]' : 'border-[#BFC6CC] hover:border-[#397DFF]'}`}>
              <div className="flex items-center gap-3">
                <FileText className={`w-5 h-5 ${methodeConsommation === 'manuel' ? 'text-[#397DFF]' : 'text-[#687175]'}`} />
                <div>
                  <div className="font-medium text-[#191D1E]">Saisie Manuelle</div>
                  <div className="text-xs text-[#687175]">Remplir mois par mois</div>
                </div>
              </div>
            </button>
          </div>

          {methodeConsommation === 'excel' ? (
            <div className="space-y-6">
              <div className="p-6 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#397DFF]" />
                    <div>
                      <div className="text-sm font-medium text-[#191D1E]">Template Excel</div>
                      <div className="text-xs text-[#687175]">Format : PDL en lignes, Mois en colonnes</div>
                    </div>
                  </div>
                  <button type="button" onClick={downloadTemplate} className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white px-4 py-2 rounded-lg text-sm font-medium">
                    <Download className="w-4 h-4" /> Télécharger Template
                  </button>
                </div>
                <div className="text-xs text-[#687175]">
                  {(rapportType === 'mono' ? formData.pdls.length : formData.sites.reduce((sum, s) => sum + s.pdls.length, 0))} PDL × {generateMonthsForPeriod().length} mois
                </div>
              </div>

              <div className="mb-6 space-y-4">
                <div className="p-4 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#191D1E]">Import Template Standard</div>
                      <div className="text-xs text-[#687175] mt-1">Format : Date | PDL1 | PDL2 | ... (généré par template)</div>
                    </div>
                    <label className="flex items-center gap-2 px-4 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg cursor-pointer text-sm">
                      <Upload className="w-4 h-4" />
                      Charger Template
                      <input type="file" accept=".xlsx,.xls" className="hidden" />
                    </label>
                  </div>
                </div>
                <div className="p-4 bg-[#E8F4FD] border border-[#397DFF] rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#191D1E]">Import Export Outil Suivi</div>
                      <div className="text-xs text-[#687175] mt-1">Format : PDL | Date | Consommation (détection auto colonnes)</div>
                    </div>
                    <label className="flex items-center gap-2 px-4 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg cursor-pointer text-sm">
                      <Upload className="w-4 h-4" />
                      Charger Export
                      <input type="file" accept=".xlsx,.xls" onChange={handleImportConsommationsOutil} className="hidden" />
                    </label>
                  </div>
                </div>
              </div>

              {Object.keys(consommationsParPDL).length > 0 && (
                <div className="mb-6">
                  <button type="button" onClick={() => setShowTableauRecap(!showTableauRecap)} className="w-full py-3 border-2 border-[#397DFF] text-[#397DFF] hover:bg-[#E8F4FD] rounded-lg font-medium transition-all">
                    {showTableauRecap ? '← Masquer' : '→ Afficher'} Vue Tableau (Contrôle Qualité)
                  </button>
                </div>
              )}

              {showTableauRecap && (
                <div className="mb-6">
                  <TableauRecapConsommations
                    consommationsParPDL={consommationsParPDL}
                    pdls={rapportType === 'mono' ? formData.pdls : formData.sites.flatMap(s => s.pdls)}
                    mois={generateMonthsForPeriod()}
                    onUpdateConsommation={updateConsommation}
                  />
                </div>
              )}

              <div className="p-8 border-2 border-dashed border-[#BFC6CC] rounded-lg text-center">
                <Upload className="w-12 h-12 text-[#687175] mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-[#397DFF] hover:text-[#4672B6] font-medium">Cliquer pour charger le fichier Excel</span>
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportConsommationsExcel} />
                </label>
                <p className="text-sm text-[#687175] mt-2">ou glissez-déposez votre fichier ici</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {rapportType === 'mono' ? (
                formData.pdls.map((pdl, pdlIndex) => (
                  <div key={pdlIndex} className="border border-[#BFC6CC] rounded-lg overflow-hidden">
                    <div className="bg-[#F5F6F9] px-6 py-3 border-b border-[#BFC6CC]">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-mono font-semibold text-[#191D1E]">{pdl.pdl}</span>
                          <span className="text-sm text-[#687175] ml-4">{pdl.puissance_souscrite} kVA • {pdl.code_naf}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 text-sm text-[#397DFF] hover:text-[#4672B6] cursor-pointer">
                            <Upload className="w-4 h-4" />
                            Import Excel
                            <input type="file" accept=".xlsx,.xls" className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = (evt) => {
                                  try {
                                    const data = new Uint8Array(evt.target.result);
                                    const workbook = XLSX.read(data, { type: 'array' });
                                    const ws = workbook.Sheets[workbook.SheetNames[0]];
                                    const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
                                    const values = [];
                                    jsonData.forEach(row => {
                                      if (Array.isArray(row)) {
                                        row.forEach(cell => {
                                          if (cell !== undefined && cell !== null && cell !== '') {
                                            const val = parseFloat(String(cell).replace(',', '.').replace(/\s/g, ''));
                                            if (!isNaN(val)) values.push(val);
                                          }
                                        });
                                      }
                                    });
                                    if (values.length > 0) {
                                      const months = generateMonthsForPeriod();
                                      const newConso = { ...consommationsParPDL };
                                      if (!newConso[pdl.pdl]) newConso[pdl.pdl] = {};
                                      values.slice(0, months.length).forEach((val, idx) => {
                                        newConso[pdl.pdl][months[idx].value] = val;
                                      });
                                      setConsommationsParPDL(newConso);
                                      alert(`✓ ${Math.min(values.length, months.length)} valeurs importées pour PDL ${pdl.pdl}`);
                                    }
                                  } catch (error) {
                                    alert('❌ Erreur lecture fichier');
                                  }
                                };
                                reader.readAsArrayBuffer(file);
                                e.target.value = '';
                              }} />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-sm text-[#687175] mb-4">💡 <strong>Astuce :</strong> Sélectionnez la première cellule, collez vos 12 valeurs (Ctrl+V), elles se rempliront automatiquement</p>
                      <div className="max-w-md mx-auto">
                        <table className="w-full border border-[#BFC6CC]">
                          <thead className="bg-[#F5F6F9]">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold text-[#191D1E] border-r border-[#BFC6CC]">Mois</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold text-[#191D1E]">Consommation (kWh)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {generateMonthsForPeriod().map((month, monthIndex) => (
                              <tr key={monthIndex} className="border-t border-[#BFC6CC] hover:bg-[#F5F6F9]">
                                <td className="px-4 py-2 text-sm text-[#687175] border-r border-[#BFC6CC]">{month.label}</td>
                                <td className="px-2 py-1">
                                  <input type="number" step="0.01"
                                    value={consommationsParPDL[pdl.pdl]?.[month.value] || ''}
                                    onChange={(e) => updateConsommation(pdl.pdl, month.value, e.target.value)}
                                    onPaste={(e) => {
                                      e.preventDefault();
                                      const pastedData = e.clipboardData.getData('text');
                                      const values = pastedData.split(/[\t\n\r]+/).map(v => v.trim()).filter(v => v).map(v => parseFloat(v.replace(',', '.').replace(/\s/g, ''))).filter(v => !isNaN(v));
                                      if (values.length > 0) {
                                        const months = generateMonthsForPeriod();
                                        const newConso = { ...consommationsParPDL };
                                        if (!newConso[pdl.pdl]) newConso[pdl.pdl] = {};
                                        values.forEach((val, idx) => {
                                          if (monthIndex + idx < months.length) {
                                            newConso[pdl.pdl][months[monthIndex + idx].value] = val;
                                          }
                                        });
                                        setConsommationsParPDL(newConso);
                                      }
                                    }}
                                    placeholder="0"
                                    className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-[#397DFF] font-mono text-sm" />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 pt-4 border-t border-[#BFC6CC] flex justify-between text-sm">
                        <span className="text-[#687175]">Total consommation :</span>
                        <span className="font-mono font-semibold text-[#191D1E]">
                          {Object.values(consommationsParPDL[pdl.pdl] || {}).reduce((sum, v) => sum + v, 0).toFixed(0)} kWh
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                formData.sites.map((site, siteIdx) => (
                  <div key={siteIdx} className="border border-[#BFC6CC] rounded-lg overflow-hidden">
                    <div className="bg-[#397DFF] text-white px-6 py-3">
                      <div className="font-semibold">{site.nom_site || `Site ${siteIdx + 1}`}</div>
                      <div className="text-sm opacity-90 font-mono">{site.siret}</div>
                    </div>
                    {site.pdls.map((pdl, pdlIdx) => (
                      <div key={pdlIdx} className="border-t border-[#BFC6CC]">
                        <div className="bg-[#F5F6F9] px-6 py-2 border-b border-[#BFC6CC] text-sm">
                          <span className="font-mono font-semibold text-[#191D1E]">{pdl.pdl}</span>
                          <span className="ml-4 text-[#687175]">{pdl.puissance_souscrite} kVA • {pdl.code_naf}</span>
                        </div>
                        <div className="p-6">
                          <div className="max-w-md mx-auto">
                            <table className="w-full border border-[#BFC6CC]">
                              <thead className="bg-[#F5F6F9]">
                                <tr>
                                  <th className="px-4 py-2 text-left text-sm font-semibold text-[#191D1E] border-r border-[#BFC6CC]">Mois</th>
                                  <th className="px-4 py-2 text-left text-sm font-semibold text-[#191D1E]">kWh</th>
                                </tr>
                              </thead>
                              <tbody>
                                {generateMonthsForPeriod().map((month, monthIdx) => (
                                  <tr key={monthIdx} className="border-t border-[#BFC6CC]">
                                    <td className="px-4 py-2 text-sm text-[#687175] border-r border-[#BFC6CC]">{month.label}</td>
                                    <td className="px-2 py-1">
                                      <input type="number" step="0.01"
                                        value={consommationsParPDL[pdl.pdl]?.[month.value] || ''}
                                        onChange={(e) => updateConsommation(pdl.pdl, month.value, e.target.value)}
                                        onPaste={(e) => {
                                          e.preventDefault();
                                          const pastedData = e.clipboardData.getData('text');
                                          const values = pastedData.split(/[\t\n\r]+/).map(v => v.trim()).filter(v => v).map(v => parseFloat(v.replace(',', '.').replace(/\s/g, ''))).filter(v => !isNaN(v));
                                          if (values.length > 0) {
                                            const months = generateMonthsForPeriod();
                                            const newConso = { ...consommationsParPDL };
                                            if (!newConso[pdl.pdl]) newConso[pdl.pdl] = {};
                                            values.forEach((val, idx) => {
                                              if (monthIdx + idx < months.length) {
                                                newConso[pdl.pdl][months[monthIdx + idx].value] = val;
                                              }
                                            });
                                            setConsommationsParPDL(newConso);
                                          }
                                        }}
                                        className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-[#397DFF] font-mono text-sm" />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-4 text-sm text-right text-[#687175]">
                            Total : {Object.values(consommationsParPDL[pdl.pdl] || {}).reduce((sum, v) => sum + v, 0).toFixed(0)} kWh
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      <div className="flex justify-between gap-3 pt-6">
        <button onClick={() => setStep(rapportType === 'mono' ? 3 : 4)} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9]">← Retour</button>
        <div className="flex gap-3">
          <button type="button" onClick={sauvegarderBrouillon} className="px-6 py-3 border border-[#F9C80E] text-[#F9C80E] font-medium rounded-lg hover:bg-[#FFF9E6]">💾 Enregistrer Brouillon</button>
          <button onClick={() => setStep(6)} className="bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-3 px-8 rounded-lg">Suivant : Validation →</button>
        </div>
      </div>
    </div>
  );
};

export default Step5Consommations;
